import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./MemosAndNotes.module.sass";
import Card from "../../../components/Card";
import TextInput from "../../../components/TextInput";
import Editor from "../../../components/Editor";
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { getStudentMemos, createStudentMemo } from "../../../services/studentMemo.service";
import { showSuccess, showError } from "../../../services/toast.service";
import { useAuth } from "../../../contexts/AuthContext";

const MemosAndNotes = ({ className, studentId }) => {
    const { currentUser } = useAuth();
    // État pour l'éditeur de texte
    const [content, setContent] = useState(() => {
        const contentState = ContentState.createFromText('');
        return EditorState.createWithContent(contentState);
    });
    
    // États pour le formulaire et les mémos
    const [topic, setTopic] = useState('');
    const [memos, setMemos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Charger les mémos de l'étudiant
    useEffect(() => {
        const fetchMemos = async () => {
            if (!studentId) return;
            
            setLoading(true);
            
            try {
                const studentMemos = await getStudentMemos(studentId);
                setMemos(studentMemos || []);
            } catch (error) {
            } finally {
                setTimeout(() => {
                    setLoading(false);
                }, 300);
            }
        };

        fetchMemos();
    }, [studentId]);

    // Gérer la soumission d'un nouveau mémo
    const handleSubmit = async () => {
        if (!topic.trim()) {
            showError("Veuillez saisir un sujet pour le mémo");
            return;
        }

        if (!studentId) {
            showError("Impossible de créer un mémo: ID étudiant manquant");
            return;
        }

        setSubmitting(true);

        try {
            // Convertir le contenu de l'éditeur en texte
            const contentState = content.getCurrentContent();
            const rawContent = convertToRaw(contentState);
            const contentText = contentState.getPlainText();

            if (!contentText.trim()) {
                showError("Veuillez ajouter du contenu au mémo");
                setSubmitting(false);
                return;
            }

            // Préparer les données du mémo
            const memoData = {
                student_id: studentId,
                topic: topic,
                content: JSON.stringify(rawContent), // Stocker le contenu au format JSON pour préserver le formatage
                created_by: currentUser?.id
            };

            // Créer le mémo
            const newMemo = await createStudentMemo(memoData);
            
            // Mettre à jour la liste des mémos
            setMemos(prevMemos => [newMemo, ...prevMemos]);
            
            // Réinitialiser le formulaire
            setTopic('');
            const emptyContentState = ContentState.createFromText('');
            setContent(EditorState.createWithContent(emptyContentState));
            
            showSuccess("Mémo créé avec succès");
        } catch (error) {
            console.error("Erreur lors de la création du mémo:", error);
            showError("Impossible de créer le mémo: " + (error.message || "Erreur inconnue"));
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            {/* Liste des mémos existants */}
            <Card 
                className={cn(styles.card, className)} 
                title="Mémos" 
                classTitle="title-red"
            >
                <div className={styles.description}>
                    <div className={styles.memosList}>
                        {loading ? (
                            <div className={styles.loading}>
                                <div className="spinner-large" style={{ margin: '20px auto' }} />
                                <div>Chargement des mémos...</div>
                            </div>
                        ) : memos.length === 0 ? (
                            <div className={styles.empty}>Aucun mémo pour cet étudiant</div>
                        ) : (
                            memos.map((memo) => (
                                <div key={memo.id} className={styles.memoItem}>
                                    <div className={styles.memoHeader}>
                                        <div className={styles.memoDate}>
                                            {formatDate(memo.created)}
                                        </div>
                                        {memo.expand?.created_by && (
                                            <div className={styles.memoAuthor}>
                                                par {memo.expand.created_by.first_name} {memo.expand.created_by.last_name}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.login}>{memo.topic}</div>
                                    <div className={styles.memoContent}>
                                        {(() => {
                                            try {
                                                // Essayer de parser le contenu JSON
                                                const contentObj = JSON.parse(memo.content);
                                                // Si c'est un objet Draft.js, extraire le texte brut
                                                if (contentObj.blocks) {
                                                    return contentObj.blocks.map((block, index) => (
                                                        <p key={index}>{block.text}</p>
                                                    ));
                                                }
                                                // Sinon, afficher le contenu tel quel
                                                return <p>{memo.content}</p>;
                                            } catch (e) {
                                                // Si ce n'est pas du JSON valide, afficher tel quel
                                                return <p>{memo.content}</p>;
                                            }
                                        })()}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </Card>

            {/* Formulaire de création de mémo */}
            <Card
                className={cn(styles.card, className)}
                title="Nouveau mémo"
                classTitle="title-green"
            >
                <div className={styles.description}>
                    <TextInput
                        className={styles.field}
                        label="Sujet"
                        name="topic"
                        type="text"
                        placeholder="Sujet du mémo"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        required
                    />
                    <Editor
                        state={content}
                        onChange={setContent}
                        classEditor={styles.editor}
                        label="Contenu"
                        placeholder="Ajoutez le contenu du mémo ici..."
                    />
                    
                    <div className={styles.actions} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button
                            className="button-green"
                            onClick={handleSubmit}
                            disabled={submitting}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            {submitting ? 'Création en cours...' : 'Créer le mémo'}
                            {submitting && <div className="spinner-small" />}
                        </button>
                    </div>
                </div>
            </Card>
        </>
    );
};

export default MemosAndNotes;
